/**
 * Playback: play, pause, step a day, and scrub to anywhere in the run.
 *
 * Rewind is exact rather than approximate because the entire run is computed before the
 * first frame is drawn — the scrubber moves an index into a finished log, so day 3 after
 * visiting day 140 is identical to day 3 the first time. Event days are marked on the
 * track so there is somewhere to aim for.
 */

import { useEffect } from 'react';
import type { RunLog } from 'walkout-engine';
import { SPEEDS, type Playback, type Speed } from '../state/useSimulation.ts';
import './PlaybackControls.css';

export function PlaybackControls({ run, playback }: { run: RunLog; playback: Playback }) {
  const { day, playing, speed, lastDay } = playback;

  // Keyboard control. Space is what everyone tries first; arrows are what they try second.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      switch (event.key) {
        case ' ':
          event.preventDefault();
          playback.toggle();
          break;
        case 'ArrowRight':
          event.preventDefault();
          playback.step(event.shiftKey ? 10 : 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          playback.step(event.shiftKey ? -10 : -1);
          break;
        case 'Home':
          event.preventDefault();
          playback.restart();
          break;
        case 'End':
          event.preventDefault();
          playback.seek(lastDay);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playback, lastDay]);

  // Days worth scrubbing to: anything the model considered a headline.
  const marks = run.days
    .filter((d) => d.events.some((e) => e.severity === 'major'))
    .map((d) => d.day);

  return (
    <div className="playback">
      <div className="playback__buttons">
        <button type="button" onClick={playback.restart} title="Back to day 0 (Home)">
          ⏮
        </button>
        <button type="button" onClick={() => playback.step(-1)} title="Back one day (←)">
          ◀
        </button>
        <button
          type="button"
          className="playback__play"
          onClick={playback.toggle}
          title={playing ? 'Pause (space)' : 'Play (space)'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button type="button" onClick={() => playback.step(1)} title="Forward one day (→)">
          ▶
        </button>
        <button type="button" onClick={() => playback.seek(lastDay)} title="Jump to the end (End)">
          ⏭
        </button>
      </div>

      <div className="playback__track">
        <input
          className="playback__scrub"
          type="range"
          min={0}
          max={lastDay}
          step={1}
          value={day}
          onChange={(event) => playback.seek(Number(event.target.value))}
          aria-label={`Day ${day} of ${lastDay}`}
        />
        <div className="playback__marks" aria-hidden="true">
          {marks.map((markDay) => (
            <button
              key={markDay}
              type="button"
              className="playback__mark"
              style={{ left: `${lastDay === 0 ? 0 : (markDay / lastDay) * 100}%` }}
              onClick={() => playback.seek(markDay)}
              tabIndex={-1}
              title={`Day ${markDay}`}
            />
          ))}
        </div>
      </div>

      <div className="playback__speed">
        <span className="label">Speed</span>
        {SPEEDS.map((option: Speed) => (
          <button
            key={option}
            type="button"
            className={`playback__speed-button${speed === option ? ' playback__speed-button--active' : ''}`}
            onClick={() => playback.setSpeed(option)}
          >
            {option}×
          </button>
        ))}
      </div>
    </div>
  );
}
